/* eslint-disable no-case-declarations */
 
 
 
 
/* eslint-disable @typescript-eslint/no-explicit-any */
 
/**
 * Dev Mode Service
 * Manages developer mode settings and preferences
 */

import { debugService } from './debugService';

export interface DevModeSettings {
  enabled: boolean;
  disableTutorial: boolean;
  panels: {
    gameState: boolean;
    events: boolean;
    time: boolean;
    cheats: boolean;
    products: boolean;
    sales: boolean;
    competitors: boolean;
    console: boolean;
    performance: boolean;
    formulas: boolean;
  };
  layout: {
    activeTab: string;
    panelPositions: Record<string, { x: number; y: number }>;
  };
  shortcuts: {
    toggleDevMode: string;
    quickSave: string;
    quickLoad: string;
  };
  console: {
    history: string[];
    maxHistory: number;
  };
}

const DEFAULT_SETTINGS: DevModeSettings = {
  enabled: false,
  disableTutorial: true,
  panels: {
    gameState: true,
    events: true,
    time: true,
    cheats: true,
    products: true,
    sales: true,
    competitors: false,
    console: true,
    performance: false,
    formulas: true,
  },
  layout: {
    activeTab: 'gameState',
    panelPositions: {},
  },
  shortcuts: {
    toggleDevMode: 'F12',
    quickSave: 'F5',
    quickLoad: 'F9',
  },
  console: {
    history: [],
    maxHistory: 100,
  },
};

const STORAGE_KEY = 'smartphone-simulator-dev-mode';

class DevModeService {
  private settings: DevModeSettings;
  private listeners: Set<(settings: DevModeSettings) => void> = new Set();
  private toastCallback: ((message: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void) | null = null;
  private gameRefs: Record<string, unknown> = {};

  constructor() {
    this.settings = this.loadSettings();
    
    // If dev mode was previously enabled, wait for game refs to be set
    if (this.settings.enabled) {
      console.log('Dev mode was previously enabled. Press F12 to toggle.');
    }
  }

  setToastCallback(callback: (message: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void) {
    this.toastCallback = callback;
  }

  setGameRefs(refs: Record<string, unknown>) {
    this.gameRefs = refs;
    // Always initialize but don't auto-enable
    debugService.initialize(refs as Parameters<typeof debugService.initialize>[0]);
    
    // If dev mode is enabled in settings, enable debug functions
    if (this.settings.enabled) {
      debugService.enable();
    }
  }

  private loadSettings(): DevModeSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Deep merge to handle nested objects like panels
        return {
          ...DEFAULT_SETTINGS,
          ...parsed,
          panels: {
            ...DEFAULT_SETTINGS.panels,
            ...(parsed.panels || {})
          },
          layout: {
            ...DEFAULT_SETTINGS.layout,
            ...(parsed.layout || {})
          },
          shortcuts: {
            ...DEFAULT_SETTINGS.shortcuts,
            ...(parsed.shortcuts || {})
          },
          console: {
            ...DEFAULT_SETTINGS.console,
            ...(parsed.console || {})
          }
        };
      }
    } catch (error) {
      console.error('Failed to load dev mode settings:', error);
    }
    return DEFAULT_SETTINGS;
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to save dev mode settings:', error);
    }
  }


  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.settings));
  }

  // Public API
  getSettings(): DevModeSettings {
    return { ...this.settings };
  }

  updateSettings(updates: Partial<DevModeSettings>): void {
    this.settings = { ...this.settings, ...updates };
    this.saveSettings();
  }

  toggleDevMode(): void {
    this.settings.enabled = !this.settings.enabled;
    this.saveSettings();

    if (this.settings.enabled) {
      // Enable debug functions
      if (this.gameRefs) {
        debugService.initialize(this.gameRefs as Parameters<typeof debugService.initialize>[0]);
      }
      debugService.enable();
      
      // Show toast notification
      if (this.toastCallback) {
        this.toastCallback({
          type: 'info',
          title: 'Dev Mode Enabled',
          message: 'Debug functions available in browser console. Type debug.help() for commands.',
          duration: 5000
        });
      }
    } else {
      // Disable debug functions
      debugService.disable();
      
      // Show toast notification
      if (this.toastCallback) {
        this.toastCallback({
          type: 'info',
          title: 'Dev Mode Disabled',
          message: 'Debug functions removed from browser console.',
          duration: 3000
        });
      }
    }
  }

  togglePanel(panel: keyof DevModeSettings['panels']): void {
    this.settings.panels[panel] = !this.settings.panels[panel];
    this.saveSettings();
  }

  toggleDisableTutorial(): void {
    this.settings.disableTutorial = !this.settings.disableTutorial;
    this.saveSettings();
  }

  setActiveTab(tab: string): void {
    this.settings.layout.activeTab = tab;
    this.saveSettings();
  }

  updatePanelPosition(panel: string, position: { x: number; y: number }): void {
    this.settings.layout.panelPositions[panel] = position;
    this.saveSettings();
  }

  addToConsoleHistory(command: string): void {
    this.settings.console.history.unshift(command);
    if (this.settings.console.history.length > this.settings.console.maxHistory) {
      this.settings.console.history.pop();
    }
    this.saveSettings();
  }

  getConsoleHistory(): string[] {
    return [...this.settings.console.history];
  }

  subscribe(listener: (settings: DevModeSettings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Debug commands
  executeCommand(command: string): { success: boolean; message: string; data?: any } {
    const parts = command.trim().split(' ');
    const cmd = parts[0].toLowerCase();

    switch (cmd) {
      case '/help':
        return {
          success: true,
          message: 'Available commands:\n' +
            '/help - Show this help\n' +
            '/clear - Clear console\n' +
            '/panels - List panel states\n' +
            '/toggle [panel] - Toggle a panel\n' +
            '/reset - Reset dev mode settings',
        };

      case '/clear':
        this.settings.console.history = [];
        this.saveSettings();
        return { success: true, message: 'Console cleared' };

      case '/panels':
        return {
          success: true,
          message: 'Panel states:',
          data: this.settings.panels,
        };

      case '/toggle':
        const panel = parts[1];
        if (panel && panel in this.settings.panels) {
          this.togglePanel(panel as keyof DevModeSettings['panels']);
          return { success: true, message: `Toggled ${panel}` };
        }
        return { success: false, message: 'Invalid panel name' };

      case '/reset':
        this.settings = DEFAULT_SETTINGS;
        this.saveSettings();
        return { success: true, message: 'Dev mode settings reset' };

      default:
        return { success: false, message: `Unknown command: ${cmd}` };
    }
  }
}

// Create singleton instance
export const devModeService = new DevModeService();